import { useLoginByCredentialsMutation } from "@generated/graphql";
import { LoadingButton } from "@mui/lab";
import { Stack, Typography } from "@mui/material";
import Form from "components/form/Form";
import TextFieldControl from "components/form/TextFieldControl";
import ROUTES from "constants/routes";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { setAuthCookies } from "helpers/cookieHelper";
import { useContext } from "react";
import { useForm } from "react-hook-form";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";

type SignInInputs = {
  email: string;
  password: string;
};

const FIELD_LABEL_PROPS = {
  variant: "body16rg" as const,
  sx: {
    color: "base.500",
    fontWeight: 600,
    mb: 0.5,
  },
};

const UserLogin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { addToast } = useContext(ToastContext);
  const form = useForm<SignInInputs>({
    defaultValues: {
      email: "",
      password: "",
    },
  });
  const { handleSubmit, setError } = form;

  const [login, { loading }] = useLoginByCredentialsMutation();

  const onSubmit = (formData: SignInInputs) => {
    login({
      variables: formData,
    })
      .then((res) => {
        const accessToken = res.data?.loginByCredentials.accessToken;
        const refreshToken = res.data?.loginByCredentials.refreshToken;

        if (!accessToken || !refreshToken) {
          addToast({ type: ToastTypeEnum.ERROR, text: "Произошла ошибка при авторизации" });
          return;
        }

        setAuthCookies([
          { name: "accessToken", value: accessToken },
          { name: "refreshToken", value: refreshToken },
        ]);
        navigate(`/${ROUTES.SELECT_COMPANY}${location.search}`, { replace: true });
      })
      .catch(() => {
        const names: Array<keyof SignInInputs> = ["email", "password"];
        names.forEach((name) => setError(name, { message: "Неверный логин или пароль" }));
      });
  };

  return (
    <Form
      form={form}
      onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        <TextFieldControl
          name='email'
          label='Почта или логин'
          type='text'
          variant='outlined'
          labelTypographyProps={FIELD_LABEL_PROPS}
          placeholder='you@company.ru'
          rules={{ required: true }}
        />

        <TextFieldControl
          name='password'
          label='Пароль'
          type='password'
          variant='outlined'
          labelTypographyProps={FIELD_LABEL_PROPS}
          placeholder='Введите пароль'
          rules={{ required: true }}
        />

        <LoadingButton
          type='submit'
          loading={loading}
          variant='contained'
          size='large'>
          Войти
        </LoadingButton>

        <Typography
          component={RouterLink}
          to={`/${ROUTES.FORGOT_PASSWORD}`}
          sx={{
            textDecoration: "none",
            color: "primary.main",
            fontSize: 16,
            fontWeight: 600,
            lineHeight: 1.3,
            "&:hover": {
              textDecoration: "underline",
            },
          }}>
          Забыли пароль?
        </Typography>
      </Stack>
    </Form>
  );
};

export default UserLogin;
